import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  ApplicationType,
  companyIdSelector,
  entitiesAtom,
  EntityApi,
  errors,
  isEmptyResponseErrorResult,
  isLoggedInAtom,
  LanguageApi,
  languagesAtom,
  Log,
  safeDataLoad,
  simpleResultErrorTechnicalError,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { GlobalAppContext } from "../../contexts";
import { useSimpleNavigation } from "../../navigation";
import { useAuthActions, useProfileActions } from "../auth-profile";
import { useCompanyScopeSelect } from "./parts";

export const useCompanyScopeCtxProvider = (): void => {
  const { t } = useTranslation();
  const isLoggedIn = useRecoilValue(isLoggedInAtom);
  const { setOrChooseProfile } = useCompanyScopeSelect();
  const { fetchProfile, fetchSystemSettings } = useProfileActions();
  const companyId = useRecoilValue(companyIdSelector);
  const setEntitiesList = useSetRecoilState(entitiesAtom);
  const setLanguagesList = useSetRecoilState(languagesAtom);
  const { navigateLogin } = useSimpleNavigation();
  const { handleLogout, setLoginErrors } = useAuthActions();
  const { applicationType } = useContext(GlobalAppContext);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const fetchLanguages = async (): Promise<void> => {
      const languages = await LanguageApi.getAll();
      setLanguagesList(languages ?? []);
    };

    const fetchEntities = async (): Promise<void> => {
      const entities = await EntityApi.getAll();
      setEntitiesList(entities);
    };

    const loadData = async (): Promise<void> => {
      // `languagesAtom` is read by exactly one screen - the admin app's alert configuration - and
      // these two calls are serial, so every other app was paying an extra round trip on the login
      // critical path for data it never reads.
      if (applicationType === ApplicationType.Admin) {
        await fetchLanguages();
      }

      await fetchEntities();
    };

    safeDataLoad({
      fn: loadData,
      t,
      fallbackKey: errors.auth.unhandledErrorFetchingProfile,
    });
  }, [applicationType, isLoggedIn, setEntitiesList, setLanguagesList, t]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    let isCancelled = false;

    const initializeCompanyProfile = async (): Promise<void> => {
      const profileFetchResult = await fetchProfile();

      if (isCancelled) {
        return;
      }

      if (!profileFetchResult.success) {
        warnAndToastSimpleResult(profileFetchResult, t);
        setLoginErrors([t(errors.auth.failedFetchProfile)]);
        return;
      }

      if (!profileFetchResult.data) {
        warnAndToastSimpleResult(simpleResultErrorTechnicalError(t), t);
      }

      const profileDto = profileFetchResult.data;

      if (profileDto) {
        const profileChoseResult = await setOrChooseProfile(profileDto);

        if (isCancelled) {
          return;
        }

        if (!profileChoseResult.success) {
          warnAndToastSimpleResult(profileChoseResult, t);
          handleLogout();
        }
      }
    };

    Log.debug(`Start fetching profile`);
    safeDataLoad({
      fn: initializeCompanyProfile,
      t,
      fallbackKey: errors.auth.unhandledErrorFetchingProfile,
    });

    return () => {
      isCancelled = true;
    };
  }, [
    fetchProfile,
    handleLogout,
    isLoggedIn,
    navigateLogin,
    setLoginErrors,
    setOrChooseProfile,
    t,
  ]);

  useEffect(() => {
    if (!isLoggedIn || !companyId) {
      return;
    }

    let isCancelled = false;

    const fetchSettings = async (curCompanyId: number): Promise<void> => {
      Log.debug(`Start fetching system settings for company ${curCompanyId}`);

      const result = await fetchSystemSettings(curCompanyId);

      if (isCancelled) {
        return;
      }

      if (!result.success) {
        warnAndToastSimpleResult(result, t);
        setLoginErrors([t(errors.common.failedFetchSystemSettings)]);

        if (
          !isEmptyResponseErrorResult(result) &&
          applicationType !== ApplicationType.Landings
        ) {
          // Logout only on real API failures: a request that died without any
          // HTTP response (offline, canceled by navigation) says nothing about
          // the session and must not destroy it.
          handleLogout();
        }
      }
    };

    safeDataLoad({
      fn: () => fetchSettings(companyId),
      t,
      fallbackKey: errors.auth.unhandledErrorFetchingProfile,
    });

    return () => {
      isCancelled = true;
    };
  }, [
    fetchSystemSettings,
    isLoggedIn,
    companyId,
    setLoginErrors,
    t,
    handleLogout,
    applicationType,
  ]);
};
